import { submitTicket } from '../api-client'
import { saveTrackingToken } from '../storage'
import type { EventCallback, SubmitTicketData, TicketResult } from '../types'

type T = (key: string) => string

export function createTicketForm(
  projectId: string,
  t: T,
  onSuccess: (result: TicketResult) => void,
  onError: EventCallback,
): HTMLElement {
  const form = document.createElement('form')
  form.className = 'rqd-form'
  form.setAttribute('novalidate', '')

  const fields: Record<string, HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement> = {}
  const errors: Record<string, HTMLElement> = {}

  function addField(name: string, type: 'input' | 'textarea' | 'select', label: string, opts?: { placeholder?: string; required?: boolean; options?: Array<{ value: string; label: string }> }) {
    const group = document.createElement('div')
    group.className = 'rqd-form-group'

    const lbl = document.createElement('label')
    lbl.className = 'rqd-label'
    lbl.textContent = label
    group.appendChild(lbl)

    let el: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    if (type === 'textarea') {
      el = document.createElement('textarea')
      el.className = 'rqd-textarea'
    } else if (type === 'select') {
      el = document.createElement('select')
      el.className = 'rqd-select'
      if (opts?.options) {
        for (const opt of opts.options) {
          const option = document.createElement('option')
          option.value = opt.value
          option.textContent = opt.label
          el.appendChild(option)
        }
      }
    } else {
      el = document.createElement('input')
      el.className = 'rqd-input'
      el.type = name === 'email' ? 'email' : 'text'
    }

    if (opts?.placeholder && 'placeholder' in el) el.placeholder = opts.placeholder
    if (opts?.required) el.required = true
    el.name = name
    group.appendChild(el)

    const err = document.createElement('div')
    err.className = 'rqd-error-text'
    err.style.display = 'none'
    group.appendChild(err)

    fields[name] = el
    errors[name] = err
    form.appendChild(group)
  }

  // Unsaved data warning
  let hasContent = false
  function onBeforeUnload(e: BeforeUnloadEvent) {
    if (hasContent) {
      e.preventDefault()
    }
  }
  window.addEventListener('beforeunload', onBeforeUnload)
  form.addEventListener('input', () => {
    hasContent = Object.values(fields).some((f) => f.value.trim().length > 0)
  })

  addField('title', 'input', t('form.title'), { placeholder: t('form.titlePlaceholder'), required: true })
  addField('description', 'textarea', t('form.description'), { placeholder: t('form.descriptionPlaceholder') })
  addField('email', 'input', t('form.email'), { placeholder: t('form.emailPlaceholder'), required: true })
  addField('priority', 'select', t('form.priority'), {
    options: [
      { value: 'medium', label: t('form.priorityMedium') },
      { value: 'low', label: t('form.priorityLow') },
      { value: 'high', label: t('form.priorityHigh') },
      { value: 'critical', label: t('form.priorityCritical') },
    ],
  })

  const submitBtn = document.createElement('button')
  submitBtn.type = 'submit'
  submitBtn.className = 'rqd-btn rqd-btn-primary'
  submitBtn.textContent = t('form.submit')
  form.appendChild(submitBtn)

  function validate(): boolean {
    let valid = true
    const title = (fields.title as HTMLInputElement).value.trim()
    const email = (fields.email as HTMLInputElement).value.trim()

    // Clear errors
    for (const err of Object.values(errors)) {
      err.style.display = 'none'
      err.textContent = ''
    }

    if (!title) {
      errors.title.textContent = t('error.required')
      errors.title.style.display = 'block'
      valid = false
    } else if (title.length < 5) {
      errors.title.textContent = t('error.titleMin')
      errors.title.style.display = 'block'
      valid = false
    }

    if (!email) {
      errors.email.textContent = t('error.required')
      errors.email.style.display = 'block'
      valid = false
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email.textContent = t('error.emailInvalid')
      errors.email.style.display = 'block'
      valid = false
    }

    return valid
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault()
    if (!validate()) return

    submitBtn.disabled = true
    submitBtn.textContent = t('form.submitting')

    const data: SubmitTicketData = {
      title: (fields.title as HTMLInputElement).value.trim(),
      description: (fields.description as HTMLTextAreaElement).value.trim() || undefined,
      email: (fields.email as HTMLInputElement).value.trim(),
      priority: (fields.priority as HTMLSelectElement).value as SubmitTicketData['priority'],
    }

    try {
      const result = await submitTicket(projectId, data)
      if (result.trackingToken) {
        saveTrackingToken(projectId, result.trackingToken)
      }
      hasContent = false
      window.removeEventListener('beforeunload', onBeforeUnload)
      onSuccess(result)
    } catch (err) {
      onError(err)
      submitBtn.disabled = false
      submitBtn.textContent = t('form.submit')
    }
  })

  return form
}

export function createSuccessView(
  result: TicketResult,
  t: T,
  onClose: () => void,
  onTrack?: () => void,
): HTMLElement {
  const div = document.createElement('div')
  div.className = 'rqd-success'

  const icon = document.createElement('div')
  icon.className = 'rqd-success-icon'
  icon.textContent = '\u2705'
  div.appendChild(icon)

  const heading = document.createElement('h3')
  heading.textContent = t('success.title')
  div.appendChild(heading)

  const num = document.createElement('p')
  num.textContent = t('success.ticketNumber') + result.ticketNumber
  div.appendChild(num)

  if (result.trackingToken) {
    const hint = document.createElement('p')
    hint.textContent = t('success.trackingHint')
    hint.style.fontSize = '13px'
    hint.style.color = 'var(--rqd-text-secondary)'
    div.appendChild(hint)

    const tokenBox = document.createElement('div')
    tokenBox.className = 'rqd-token-box'
    tokenBox.textContent = result.trackingToken
    div.appendChild(tokenBox)

    const copyBtn = document.createElement('button')
    copyBtn.className = 'rqd-btn rqd-btn-secondary'
    copyBtn.textContent = t('success.copyToken')
    copyBtn.style.marginBottom = '8px'
    copyBtn.addEventListener('click', () => {
      navigator.clipboard.writeText(result.trackingToken!).then(() => {
        copyBtn.textContent = t('success.copied')
        setTimeout(() => { copyBtn.textContent = t('success.copyToken') }, 2000)
      })
    })
    div.appendChild(copyBtn)

    if (onTrack) {
      const trackBtn = document.createElement('button')
      trackBtn.className = 'rqd-btn rqd-btn-primary'
      trackBtn.textContent = t('success.trackNow')
      trackBtn.style.marginTop = '4px'
      trackBtn.addEventListener('click', onTrack)
      div.appendChild(trackBtn)
    }
  }

  const closeBtn = document.createElement('button')
  closeBtn.className = 'rqd-btn rqd-btn-secondary'
  closeBtn.textContent = t('success.close')
  closeBtn.style.marginTop = '8px'
  closeBtn.addEventListener('click', onClose)
  div.appendChild(closeBtn)

  return div
}
